import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ArrowLeft, Building2, MapPin, Users, Award } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';

// Installation Types
const installationTypes = [
  {
    id: 'corporate',
    title: 'Corporate Offices',
    description: 'Complete flooring solutions for headquarters, regional offices, and co-working spaces.',
    image: '/Assets/images/custom/corporate-offices.jpg',
    scope: ['Full floor buildouts', 'Phased renovations', 'Tenant improvements', 'After-hours installation'],
    icon: Building2
  },
  {
    id: 'retail',
    title: 'Retail Environments',
    description: 'High-impact flooring installations that enhance the shopping experience.',
    image: '/Assets/images/custom/retail-environments.jpg',
    scope: ['New store construction', 'Remodels & refreshes', 'Multi-location rollouts', 'Quick turnaround'],
    icon: MapPin
  },
  {
    id: 'hospitality',
    title: 'Hospitality & Entertainment',
    description: 'Premium flooring for hotels, restaurants, and entertainment venues.',
    image: '/Assets/images/custom/hospitality-entertainment.jpg',
    scope: ['Lobby & common areas', 'Guest rooms', 'Restaurants & bars', 'Event spaces'],
    icon: Users
  },
  {
    id: 'institutional',
    title: 'Institutional Facilities',
    description: 'Specialized installations for healthcare, education, and government facilities.',
    image: '/Assets/images/custom/institutional-facilities.jpg',
    scope: ['Healthcare facilities', 'Educational buildings', 'Government offices', 'Athletic facilities'],
    icon: Award
  }
];

// Capabilities
const capabilities = [
  {
    title: 'National Coverage',
    description: 'Installation teams across the country for consistent quality everywhere.',
    stat: '50 States'
  },
  {
    title: 'Project Management',
    description: 'Dedicated project managers from start to finish.',
    stat: 'Single Point'
  },
  {
    title: 'Certified Installers',
    description: 'Factory-trained and certified installation professionals.',
    stat: 'Expert Installers'
  }
];

// Installation Process
const installationProcess = [
  {
    step: 1,
    title: 'Site Assessment',
    description: 'Thorough evaluation of existing conditions and requirements'
  },
  {
    step: 2,
    title: 'Project Planning',
    description: 'Detailed scheduling and logistics coordination'
  },
  {
    step: 3,
    title: 'Material Staging',
    description: 'Products delivered and acclimated on-site'
  },
  {
    step: 4,
    title: 'Surface Preparation',
    description: 'Subfloor preparation and moisture testing'
  },
  {
    step: 5,
    title: 'Installation',
    description: 'Professional installation by certified teams'
  },
  {
    step: 6,
    title: 'Quality Inspection',
    description: 'Final walkthrough and punch list completion'
  }
];

const CustomInstallationsPage = () => {
  return (
    <Layout>
      <SEO 
        title="Large-Scale Installations"
        description="Expert flooring installers cover all 50 states. From corporate offices to retail rollouts, our certified installation teams deliver professional results on time and on budget."
        canonicalUrl="/custom/installations"
      />
      {/* Hero Section */}
      <section className="relative bg-primary-bg text-white py-20" data-testid="installations-hero">
        <div className="container-portico">
          <Link to="/custom" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Custom Hub
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Large-Scale Installations
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Full-scope installation services backed by national coverage, certified teams, 
              and decades of commercial flooring expertise.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary-light"
                onClick={() => document.getElementById('cta-section').scrollIntoView({ behavior: 'smooth' })}
              >
                Discuss Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/custom/portfolio?category=installations">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                  View Case Studies
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Stats */}
      <section className="py-12 bg-muted">
        <div className="container-portico">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {capabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2 whitespace-nowrap">{cap.stat}</div>
                <div className="font-semibold mb-1">{cap.title}</div>
                <div className="text-sm text-muted-foreground">{cap.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Types */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Specialized installation expertise across diverse commercial environments.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {installationTypes.map((type, index) => (
              <motion.div
                key={type.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl overflow-hidden shadow-lg border border-border"
              >
                <div className="h-56 overflow-hidden relative">
                  <img
                    src={type.image}
                    alt={type.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <type.icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-xl mb-3">{type.title}</h3>
                  <p className="text-muted-foreground mb-4">{type.description}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {type.scope.map((item) => (
                      <div key={item} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-secondary flex-shrink-0" />
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Our Installation Process
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A systematic approach ensures quality results on every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {installationProcess.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-4 rounded-xl border border-border text-center"
              >
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center mx-auto mb-3 font-bold">
                  {step.step}
                </div>
                <h3 className="font-semibold text-sm mb-1">{step.title}</h3>
                <p className="text-xs text-muted-foreground">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-Location */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Multi-Location Rollouts
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Managing flooring across dozens or hundreds of locations? Our national network 
                and centralized project management deliver consistent results everywhere.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Single Point of Contact</span>
                    <p className="text-sm text-muted-foreground">One project manager for all locations</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Standardized Quality</span>
                    <p className="text-sm text-muted-foreground">Consistent installation standards nationwide</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Flexible Scheduling</span>
                    <p className="text-sm text-muted-foreground">After-hours and weekend installation available</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">Real-Time Reporting</span>
                    <p className="text-sm text-muted-foreground">Progress tracking across all locations</p>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/custom/multi-location-rollouts.jpg"
                alt="Multi-location installation"
                className="rounded-xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta-section" className="section-padding bg-muted">
        <div className="container-portico text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Ready to Discuss Your Project?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Whether it's a single location or a nationwide rollout, 
              our team is ready to deliver exceptional results.
            </p>
            <Link to="/custom#consultation-form">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Get Started
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomInstallationsPage;
